import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import proxy from '../../api/axios';
import { formatProblemTitle } from '../../common/utils';

const ACCENT = '#137274';

const TYPE_LABEL = {
  report: 'Report',
  suggest_edit: 'Suggest edit',
  contribute_testcase: 'Test case',
};

const TYPE_COLOR = {
  report: '#cf222e',
  suggest_edit: '#d97706',
  contribute_testcase: '#137274',
};

const STATUS_COLOR = {
  open: '#d97706',
  reviewed: '#2563eb',
  resolved: '#1a7f37',
  rejected: '#8b949e',
};

const ManageProblemFeedback = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark
    ? '#0f1419'
    : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 40%, #ffffff 100%)';

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ open: 0, reviewed: 0, resolved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('open');
  const [type, setType] = useState('all');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await proxy.get('/problems/admin/feedback', {
        params: {
          status,
          type,
          q: q.trim() || undefined,
        },
      });
      setItems(data.items || []);
      setCounts(data.counts || { open: 0, reviewed: 0, resolved: 0, rejected: 0 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [status, type, q]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openTotal = useMemo(
    () => (counts.open || 0) + (counts.reviewed || 0),
    [counts],
  );

  const setStatusFor = async (id, nextStatus) => {
    setBusyId(id);
    try {
      await proxy.patch(`/problems/admin/feedback/${id}`, { status: nextStatus });
      toast.success(`Marked as ${nextStatus}`);
      if (detail?.id === id) setDetail((d) => (d ? { ...d, status: nextStatus } : d));
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feedback permanently?')) return;
    setBusyId(id);
    try {
      await proxy.delete(`/problems/admin/feedback/${id}`);
      toast.success('Deleted');
      if (detail?.id === id) setDetail(null);
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ minHeight: '100%', background: pageBg, py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Helmet>
        <title>Problem Feedback | Admin</title>
      </Helmet>

      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'flex-end' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              component="h1"
              sx={{
                fontWeight: 900,
                letterSpacing: '-0.03em',
                fontSize: { xs: '1.45rem', sm: '1.7rem' },
                mb: 0.5,
              }}
            >
              Problem feedback
            </Typography>
            <Typography sx={{ color: muted, fontSize: '0.9rem' }}>
              Reports, edit suggestions, and contributed test cases from solvers.
              {openTotal > 0 ? ` · ${openTotal} need attention` : ''}
            </Typography>
          </Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchList}
            disabled={loading}
            sx={{ textTransform: 'none', fontWeight: 700, color: ACCENT }}
          >
            Refresh
          </Button>
        </Box>

        {/* Status summary */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
            gap: 1.25,
            mb: 2.5,
          }}
        >
          {['open', 'reviewed', 'resolved', 'rejected'].map((key) => (
            <Box
              key={key}
              onClick={() => setStatus(key)}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${status === key ? alpha(STATUS_COLOR[key], 0.5) : border}`,
                bgcolor: status === key ? alpha(STATUS_COLOR[key], isDark ? 0.18 : 0.08) : surface,
                cursor: 'pointer',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: STATUS_COLOR[key],
                  mb: 0.35,
                }}
              >
                {key}
              </Typography>
              <Typography sx={{ fontWeight: 900, fontSize: '1.35rem', letterSpacing: '-0.03em' }}>
                {counts[key] || 0}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Filters */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.25,
            mb: 2,
            p: 1.5,
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            bgcolor: surface,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="reviewed">Reviewed</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Type</InputLabel>
            <Select label="Type" value={type} onChange={(e) => setType(e.target.value)}>
              <MenuItem value="all">All types</MenuItem>
              <MenuItem value="report">Report</MenuItem>
              <MenuItem value="suggest_edit">Suggest edit</MenuItem>
              <MenuItem value="contribute_testcase">Test case</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search message, user, problem…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: muted }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* List */}
        <Box
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            bgcolor: surface,
            overflow: 'hidden',
          }}
        >
          {loading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center', gap: 1.5, alignItems: 'center' }}>
              <CircularProgress size={24} sx={{ color: ACCENT }} />
              <Typography sx={{ color: muted, fontWeight: 600 }}>Loading…</Typography>
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>No feedback here</Typography>
              <Typography sx={{ color: muted, fontSize: '0.9rem' }}>
                Try another status filter or clear search.
              </Typography>
            </Box>
          ) : (
            items.map((item, idx) => (
              <Box
                key={item.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
                  gap: 1.25,
                  alignItems: 'center',
                  px: { xs: 1.75, sm: 2 },
                  py: 1.5,
                  borderTop: idx === 0 ? 'none' : `1px solid ${border}`,
                  '&:hover': {
                    bgcolor: isDark ? alpha('#fff', 0.02) : alpha(ACCENT, 0.03),
                  },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                    <Chip
                      size="small"
                      label={TYPE_LABEL[item.type] || item.type}
                      sx={{
                        height: 22,
                        fontWeight: 800,
                        fontSize: '0.68rem',
                        bgcolor: alpha(TYPE_COLOR[item.type] || ACCENT, isDark ? 0.22 : 0.12),
                        color: TYPE_COLOR[item.type] || ACCENT,
                      }}
                    />
                    <Chip
                      size="small"
                      label={item.status}
                      sx={{
                        height: 22,
                        fontWeight: 700,
                        fontSize: '0.68rem',
                        textTransform: 'capitalize',
                        bgcolor: alpha(STATUS_COLOR[item.status] || muted, isDark ? 0.22 : 0.12),
                        color: STATUS_COLOR[item.status] || muted,
                      }}
                    />
                    <Typography sx={{ fontSize: '0.72rem', color: muted, fontWeight: 600 }}>
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleString()
                        : ''}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontWeight: 750,
                      fontSize: '0.95rem',
                      letterSpacing: '-0.015em',
                      mb: 0.35,
                    }}
                  >
                    {formatProblemTitle(item.problem?.title) || 'Unknown problem'}
                  </Typography>
                  <Typography
                    sx={{
                      color: muted,
                      fontSize: '0.82rem',
                      mb: 0.75,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.45,
                    }}
                  >
                    {item.message}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: muted }}>
                    {item.user?.name || item.user?.username || 'User'}
                    {item.user?.email ? ` · ${item.user.email}` : ''}
                    {item.problem?.slug ? ` · ${item.problem.slug}` : ''}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                  <Tooltip title="View details">
                    <IconButton size="small" onClick={() => setDetail(item)} sx={{ color: ACCENT }}>
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {item.problem?.slug && (
                    <Tooltip title="Open problem">
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={`/admin/problems/${item.problem.slug}`}
                        sx={{ color: muted }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {item.status !== 'reviewed' && (
                    <Button
                      size="small"
                      disabled={busyId === item.id}
                      onClick={() => setStatusFor(item.id, 'reviewed')}
                      sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
                    >
                      Review
                    </Button>
                  )}
                  {item.status !== 'resolved' && (
                    <Button
                      size="small"
                      disabled={busyId === item.id}
                      onClick={() => setStatusFor(item.id, 'resolved')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        color: '#1a7f37',
                      }}
                    >
                      Resolve
                    </Button>
                  )}
                  {item.status !== 'rejected' && (
                    <Button
                      size="small"
                      disabled={busyId === item.id}
                      onClick={() => setStatusFor(item.id, 'rejected')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        color: muted,
                      }}
                    >
                      Reject
                    </Button>
                  )}
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      disabled={busyId === item.id}
                      onClick={() => handleDelete(item.id)}
                      sx={{ color: '#cf222e' }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Box>

      <Dialog
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${border}`,
            bgcolor: surface,
            backgroundImage: 'none',
          },
        }}
      >
        {detail && (
          <>
            <DialogTitle sx={{ fontWeight: 800, letterSpacing: '-0.02em', pr: 2 }}>
              {TYPE_LABEL[detail.type] || detail.type}
              <Typography component="span" sx={{ display: 'block', fontSize: '0.85rem', color: muted, fontWeight: 600, mt: 0.5 }}>
                {formatProblemTitle(detail.problem?.title) || 'Problem'} · {detail.status}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Typography sx={{ whiteSpace: 'pre-wrap', mb: 2, lineHeight: 1.55, fontSize: '0.92rem' }}>
                {detail.message}
              </Typography>
              {detail.type === 'contribute_testcase' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', mb: 0.5, color: muted }}>
                      INPUT
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: isDark ? '#0d1117' : '#f6f8fa',
                        border: `1px solid ${border}`,
                        fontSize: '0.8rem',
                        overflow: 'auto',
                        fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
                      }}
                    >
                      {detail.testCaseInput || '—'}
                    </Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', mb: 0.5, color: muted }}>
                      EXPECTED OUTPUT
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: isDark ? '#0d1117' : '#f6f8fa',
                        border: `1px solid ${border}`,
                        fontSize: '0.8rem',
                        overflow: 'auto',
                        fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
                      }}
                    >
                      {detail.testCaseOutput || '—'}
                    </Box>
                  </Box>
                </Box>
              )}
              <Typography sx={{ mt: 2, fontSize: '0.8rem', color: muted }}>
                From {detail.user?.name || detail.user?.username || 'User'}
                {detail.user?.email ? ` (${detail.user.email})` : ''}
                {detail.createdAt ? ` · ${new Date(detail.createdAt).toLocaleString()}` : ''}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1, flexWrap: 'wrap' }}>
              {detail.problem?.slug && (
                <Button
                  component={RouterLink}
                  to={`/admin/problems/${detail.problem.slug}`}
                  sx={{ textTransform: 'none', fontWeight: 700, mr: 'auto' }}
                >
                  Open problem
                </Button>
              )}
              <Button onClick={() => setDetail(null)} sx={{ textTransform: 'none' }}>
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => setStatusFor(detail.id, 'resolved')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: ACCENT,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
                }}
              >
                Mark resolved
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ManageProblemFeedback;
